// ============================================================================
// 本場收音的「滑鼠覆寫」持久化
//
// KKASR 的指令是**唯一真相**；即時期間依 cursor 消費，F5 恢復時只推進到 server
// 的既有尾端而不重播舊 side effect。問題是有幾件事醫師是用滑鼠做的，而滑鼠
// 不會產生 KKASR 指令：
//
//   1. 截圖卡片上的「結束編輯」（等同語音 photo over）
//   2. 臨床輔助助理面板的 × 關閉圖鑑（等同語音 show off）
//   3. 手動調整 Cleansing Level（要蓋過重整後重放的舊語音）
//
// 只放在 Vuex state 的話，F5 後會遺失已關閉的圖鑑、編輯區間與待完成拍照狀態。
// 所以這些分頁狀態要跟著 sessionId 存進 sessionStorage，重整後併回正式快照。
//
// ── 為什麼是 sessionStorage 而不是 localStorage / DB ──
//   這是「這一場、這個分頁」的畫面狀態，不是病歷資料：換一場檢查就該歸零，
//   也沒有跨裝置同步的意義。sessionStorage 的生命週期剛好就是這個範圍。
//   寫 DB 則要為純畫面狀態開欄位，而且 KKASR 指令流才是正本。
//
// ⚠️ 一律以 sessionId 當 key。接管別間檢查、或同一個病例重開一場新的收音，
//    sessionId 就換了，舊的覆寫不會沾到新的一場。
// ============================================================================

const KEY_PREFIX = 'evas2.asr.overrides.'
const RELOAD_RESUME_KEY = 'evas2.asr.reload-resume'
const FINAL_AUTH_RECOVERY_KEY = 'evas2.asr.final-auth-recovery'
export const RELOAD_RESUME_TTL_MS = 60000
export const FINAL_AUTH_RECOVERY_TTL_MS = 5 * 60 * 1000

function normalizeFileResume(fileResume) {
  if (!fileResume || !String(fileResume.name || '')) return null
  const size = Number(fileResume.size)
  const lastModified = Number(fileResume.lastModified)
  const offsetSec = Number(fileResume.offsetSec)
  const acceptedSamples = Number(fileResume.acceptedSamples)
  if (
    !Number.isFinite(size)
    || size < 0
    || !Number.isFinite(lastModified)
    || lastModified < 0
    || !Number.isFinite(offsetSec)
    || offsetSec < 0
  ) return null
  const normalized = { name: String(fileResume.name), size, lastModified, offsetSec }
  if (Number.isSafeInteger(acceptedSamples) && acceptedSamples >= 0) {
    normalized.acceptedSamples = acceptedSamples
  }
  return normalized
}

const EMPTY = () => ({
  // 手動關閉圖鑑當下的最後一個 command seq。transport 重送時 seq ≤ 這個值的 atlas_*
  // 不再顯示（seq 更大的＝重整後新講的，照常顯示）。
  atlasClearedSeq: 0,
  // 手動「結束編輯」的秒數（相對 session 起點），可能有很多次
  photoOverAtSec: [],
  // 成功拍照後自動開啟的編輯區間；保存 P 編號與語音時間，F5 後仍指向同一張
  photoSelects: [],
  // 已受理的 EVAS 拍照 command seq，避免 transport 重送造成重複拍攝
  captureHandledSeq: 0,
  // 已受理但尚未由頁面完成的拍照命令；F5 後用同一冪等鍵續送，不遺失 side effect
  captureRequests: [],
  // 手動調整清腸度時已消費到的 command seq；較舊語音重送不得覆寫手動值
  cleansingManualAfterSeq: 0,
})

function storageKey(sessionId) {
  return `${KEY_PREFIX}${sessionId}`
}

/**
 * 讀出這一場的覆寫。
 *
 * 讀不到／格式壞掉一律回空值——這是輔助狀態，寧可退回「重放結果原樣」也不能
 * 讓一筆壞掉的 JSON 把整頁弄掛。sessionStorage 本身也可能拋（無痕模式、
 * 被政策停用），所以整段包在 try 裡。
 *
 * @param {string} sessionId
 * @returns {{atlasClearedSeq:number, photoOverAtSec:number[], cleansingManualAfterSeq:number}}
 */
export function loadSessionOverrides(sessionId) {
  if (!sessionId) return EMPTY()
  try {
    const raw = window.sessionStorage.getItem(storageKey(sessionId))
    if (!raw) return EMPTY()
    const parsed = JSON.parse(raw)
    return {
      atlasClearedSeq: Number(parsed && parsed.atlasClearedSeq) || 0,
      photoOverAtSec: Array.isArray(parsed && parsed.photoOverAtSec)
        ? parsed.photoOverAtSec.map(Number).filter((n) => Number.isFinite(n))
        : [],
      photoSelects: Array.isArray(parsed && parsed.photoSelects)
        ? parsed.photoSelects
          .map((item) => ({ param: Number(item && item.param), atSec: Number(item && item.atSec) }))
          .filter((item) => item.param > 0 && Number.isFinite(item.atSec))
        : [],
      captureHandledSeq: Number(parsed && parsed.captureHandledSeq) || 0,
      captureRequests: Array.isArray(parsed && parsed.captureRequests)
        ? parsed.captureRequests
          .map((item) => ({
            ...item,
            seq: Number(item && item.seq),
            captureSessionId: item && item.captureSessionId == null
              ? null
              : String(item.captureSessionId),
            captureExaminationId: item && item.captureExaminationId == null
              ? null
              : String(item.captureExaminationId),
          }))
          .filter((item) => Number.isFinite(item.seq) && item.seq > 0)
          .sort((a, b) => a.seq - b.seq)
        : [],
      cleansingManualAfterSeq: Number(parsed && parsed.cleansingManualAfterSeq) || 0,
    }
  } catch (err) {
    return EMPTY()
  }
}

/**
 * 寫回這一場的覆寫。寫失敗只記 console——覆寫在記憶體裡已經生效，
 * 差別只是「F5 之後救不救得回來」，不值得打斷檢查。
 *
 * @param {string} sessionId
 * @param {{atlasClearedSeq:number, photoOverAtSec:number[], cleansingManualAfterSeq:number}} overrides
 */
export function saveSessionOverrides(sessionId, overrides) {
  if (!sessionId) return
  try {
    window.sessionStorage.setItem(storageKey(sessionId), JSON.stringify(overrides))
  } catch (err) {
    console.warn('[asrSession] 覆寫狀態寫入 sessionStorage 失敗', err)
  }
}

/**
 * 清掉不屬於這一場的覆寫。
 *
 * 每建立一場收音就多一個 key，而醫師的分頁可能一整天不關（一個病例接一個病例）。
 * 單筆很小，但沒有上界的累積遲早會撞到 sessionStorage 的配額，那時**寫入會拋**、
 * 連當前這一場的覆寫都存不進去。舊的 key 沒有任何用途（sessionId 不會再出現），
 * 開新場時順手掃掉。
 *
 * @param {string} keepSessionId 要保留的 sessionId
 */
export function pruneSessionOverrides(keepSessionId) {
  try {
    const storage = window.sessionStorage
    const doomed = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(KEY_PREFIX) && key !== storageKey(keepSessionId)) doomed.push(key)
    }
    // 分兩段：邊列舉邊刪會讓 index 跳號、漏掉一半
    doomed.forEach((key) => storage.removeItem(key))
  } catch (err) {
    console.warn('[asrSession] 清理舊的覆寫狀態失敗', err)
  }
}

/**
 * F5 專用、短效的一次性 session 證明。
 *
 * `/session/current` 只能回答 KKASR 現在指向哪一場，不能證明「這個新 document 就是
 * 剛才那個頁面的重載」。只靠 examination_id 會讓關掉很久的舊頁、同病例重開的新場
 * 也自動接管。因此 beforeunload 只在同一個 tab 的 sessionStorage 留一筆短效 marker；
 * 新頁仍須同時核對 server session id、examination id 與 lifecycle status。
 */
export function markSessionReloadResume({
  sessionId,
  examinationId,
  pendingFinish,
  fileResume,
  commandCursor,
} = {}, nowMs = Date.now()) {
  const normalizedSessionId = String(sessionId || '')
  const normalizedExaminationId = String(examinationId || '')
  if (!normalizedSessionId || !normalizedExaminationId) return false
  const finish = pendingFinish
    && pendingFinish.event
    && pendingFinish.event.command === 'evas_finish'
    && !pendingFinish.committing
    ? {
      event: { ...pendingFinish.event, command: 'evas_finish' },
      atSec: Number(pendingFinish.atSec) || 0,
      deadlineMs: Number(pendingFinish.deadlineMs) || 0,
      committing: false,
    }
    : null
  const resumeFile = normalizeFileResume(fileResume)
  try {
    window.sessionStorage.setItem(RELOAD_RESUME_KEY, JSON.stringify({
      sessionId: normalizedSessionId,
      examinationId: normalizedExaminationId,
      markedAtMs: Number(nowMs),
      pendingFinish: finish,
      fileResume: resumeFile,
      commandCursor: Math.max(0, Number(commandCursor) || 0),
    }))
    return true
  } catch (err) {
    console.warn('[asrSession] F5 resume marker 寫入失敗', err)
    return false
  }
}

/** 讀取但不消耗；網路暫時失敗時保留 marker，讓使用者再 F5 一次仍可恢復。 */
export function loadSessionReloadResume(nowMs = Date.now()) {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_RESUME_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const markedAtMs = Number(parsed && parsed.markedAtMs)
    const ageMs = Number(nowMs) - markedAtMs
    if (
      !parsed
      || !String(parsed.sessionId || '')
      || !String(parsed.examinationId || '')
      || !Number.isFinite(ageMs)
      || ageMs < 0
      || ageMs > RELOAD_RESUME_TTL_MS
    ) {
      window.sessionStorage.removeItem(RELOAD_RESUME_KEY)
      return null
    }
    return {
      sessionId: String(parsed.sessionId),
      examinationId: String(parsed.examinationId),
      markedAtMs,
      pendingFinish: parsed.pendingFinish || null,
      fileResume: normalizeFileResume(parsed.fileResume),
      commandCursor: Math.max(0, Number(parsed.commandCursor) || 0),
    }
  } catch (err) {
    try { window.sessionStorage.removeItem(RELOAD_RESUME_KEY) } catch (_) {}
    return null
  }
}

export function clearSessionReloadResume() {
  try {
    window.sessionStorage.removeItem(RELOAD_RESUME_KEY)
  } catch (err) {
    // 輔助 marker 清不掉時，TTL 仍會使它失效；不可中斷 ASR 主線。
  }
}

/**
 * final proxy 登入失效時保存同分頁的 exact terminal readback 身分。
 *
 * 這筆 marker 不含逐字稿、cookie 或任何 credential；它只讓重新登入後的同一病例
 * 精確讀回既有 terminal 結果。五分鐘後自動失效，避免舊分頁日後猜回過時場次。
 */
export function markFinalAuthRecovery({ sessionId, examinationId } = {}, nowMs = Date.now()) {
  const normalizedSessionId = String(sessionId || '').trim()
  const normalizedExaminationId = String(examinationId || '').trim()
  const normalizedMarkedAtMs = Number(nowMs)
  if (
    !normalizedSessionId
    || !normalizedExaminationId
    || !Number.isSafeInteger(normalizedMarkedAtMs)
    || normalizedMarkedAtMs <= 0
  ) return false
  try {
    window.sessionStorage.setItem(FINAL_AUTH_RECOVERY_KEY, JSON.stringify({
      schema: 'evas2-final-auth-recovery/v1',
      reason: 'auth_required',
      sessionId: normalizedSessionId,
      examinationId: normalizedExaminationId,
      markedAtMs: normalizedMarkedAtMs,
    }))
    return true
  } catch (err) {
    console.warn('[asrSession] final auth recovery marker 寫入失敗', err)
    return false
  }
}

/**
 * 讀取但不消耗，並保留「不存在」與「曾有但不可用」的差異。
 * 頁面只有在 status=absent 時可照一般流程開新場；invalid/expired 必須明示停止。
 */
export function inspectFinalAuthRecovery(nowMs = Date.now()) {
  try {
    const raw = window.sessionStorage.getItem(FINAL_AUTH_RECOVERY_KEY)
    if (!raw) return { status: 'absent', marker: null }
    const parsed = JSON.parse(raw)
    const markedAtMs = parsed && parsed.markedAtMs
    const ageMs = Number(nowMs) - markedAtMs
    const validShape = !!(
      !parsed
        ? false
        : parsed.schema === 'evas2-final-auth-recovery/v1'
          && parsed.reason === 'auth_required'
          && typeof parsed.sessionId === 'string'
          && typeof parsed.examinationId === 'string'
          && !!parsed.sessionId.trim()
          && !!parsed.examinationId.trim()
          && Number.isSafeInteger(markedAtMs)
          && markedAtMs > 0
          && Number.isFinite(ageMs)
          && ageMs >= 0
    )
    if (!validShape) {
      window.sessionStorage.removeItem(FINAL_AUTH_RECOVERY_KEY)
      return { status: 'invalid', marker: null }
    }
    if (ageMs > FINAL_AUTH_RECOVERY_TTL_MS) {
      window.sessionStorage.removeItem(FINAL_AUTH_RECOVERY_KEY)
      return { status: 'expired', marker: null }
    }
    return {
      status: 'valid',
      marker: {
        sessionId: parsed.sessionId,
        examinationId: parsed.examinationId,
        markedAtMs,
      },
    }
  } catch (err) {
    try { window.sessionStorage.removeItem(FINAL_AUTH_RECOVERY_KEY) } catch (_) {}
    return { status: 'invalid', marker: null }
  }
}

/** 相容既有純讀 caller；需要區分拒絕原因時應使用 inspectFinalAuthRecovery。 */
export function loadFinalAuthRecovery(nowMs = Date.now()) {
  const inspected = inspectFinalAuthRecovery(nowMs)
  return inspected.status === 'valid' ? inspected.marker : null
}

export function clearFinalAuthRecovery() {
  try {
    window.sessionStorage.removeItem(FINAL_AUTH_RECOVERY_KEY)
  } catch (err) {
    // marker 清不掉時 TTL 仍會使它失效；不可阻斷目前病例頁。
  }
}
